import type { InputHTMLAttributes, ReactNode } from "react";

/**
 * A labelled input, textarea or select. `changed` marks a value that isn't saved yet with the brand colour
 * (the same mark the save bar explains), so every screen with an explicit Save says it the same way.
 */
export function Field({ label, aside, hint, changed, children }: { label: ReactNode; aside?: ReactNode; hint?: ReactNode; changed?: boolean; children: ReactNode }) {
  return <label className="bw-area-field" data-changed={changed || undefined}>
    <span className="bw-area-field-head"><span>{label}</span>{aside}</span>
    {children}
    {hint && <small className="bw-area-hint">{hint}</small>}
  </label>;
}

/** Fields side by side. `stack` puts them in one column on a phone. */
export function FieldGroup({ columns = 2, stack, children }: { columns?: 2 | 3; stack?: boolean; children: ReactNode }) {
  return <div className="bw-area-fieldgroup" data-columns={columns} data-stack={stack || undefined}>{children}</div>;
}

export function CheckRow({ label, hint, ...input }: Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & { label: ReactNode; hint?: ReactNode }) {
  return <label className="bw-area-check"><input type="checkbox" {...input} /><span>{label}{hint && <small>{hint}</small>}</span></label>;
}

/** A bordered card. The heading, its hint and its actions share one row, then the content follows at the same gap everywhere. */
export function Card({ title, hint, description, actions, children }: { title?: ReactNode; hint?: ReactNode; description?: ReactNode; actions?: ReactNode; children?: ReactNode }) {
  return <section className="bw-area-card">
    {(title || actions) && <div className="bw-area-card-head">
      <div>{title && <h2>{title}{hint && <small> · {hint}</small>}</h2>}{description && <span>{description}</span>}</div>
      {actions}
    </div>}
    {children}
  </section>;
}

/** Two columns of cards: the wide one with the side one (a product), or a narrow list with its wide details (categories). They stack on a phone. */
export function Columns({ ratio = "main-side", children }: { ratio?: "main-side" | "side-main"; children: ReactNode }) {
  return <div className="bw-area-columns" data-ratio={ratio}>{children}</div>;
}
